import { setRuntimeConfig, setAPIToken, setRefreshToken } from '~/data/api'
import { LOCAL_STORAGE_KEYS } from '~/composables/useLocalStorageService'
import { ANALYTICS_EVENTS } from '~/types/analyticsEvents'
import Cookie from 'cookie-universal'

/*
  TRANSITIONAL — safe to delete once the legacy cookie has aged out of the
  active user base. Sessions used to be persisted in a `session_data` cookie,
  which the browser also transmits to the SSR server. This migrates any existing
  cookie session to localStorage (so the deploy does not log users out) and then
  removes the cookie so the token is no longer sent to the server.

  Returns info about what happened so the boot logic can emit an analytics event
  (used to gauge when this code is safe to remove), or null when no legacy cookie
  was present.
*/
const migrateLegacySessionCookie = (cookies, localStorage) => {
  const legacyCookie = cookies.get('session_data')

  if (!legacyCookie) return null

  const migratedSession =
    !localStorage.getItem(LOCAL_STORAGE_KEYS.session) && Boolean(legacyCookie.token)

  if (migratedSession) {
    localStorage.setItem(LOCAL_STORAGE_KEYS.session, JSON.stringify(legacyCookie))
  }

  cookies.remove('session_data', { path: '/' })

  return { migratedSession }
}

export default defineNuxtPlugin(async () => {
  const config = useRuntimeConfig()
  const cookies = Cookie()
  const router = useRouter()
  const localStorage = useLocalStorageService()

  const authStore = useAuthStore()
  const mainStore = useMainStore()

  const { getUserSettingsFromCache, updateUserSettings, getUserFresh } = useUserService()

  setRuntimeConfig(config)

  mainStore.$onAction(({ name, after }) => {
    after(() => {
      switch (name) {
        case 'updateSettings':
          if (typeof window === 'undefined') return

          if (authStore.session_data.user) {
            updateUserSettings(authStore.session_data.user.id, mainStore.settings)
          }
          break
      }
    })
  })

  authStore.$onAction(({ name, after }) => {
    after(() => {
      switch (name) {
        case 'login':
          setAPIToken(authStore.session_data.token || null)
          setRefreshToken(authStore.session_data.refresh_token || null)

          if (typeof window !== 'undefined' && authStore.currentUser) {
            const settings = getUserSettingsFromCache(authStore.currentUser.id)

            mainStore.updateSettings(settings)

            // after every successful login we fetch the latest user data immediately
            authStore.awaitAndSetLatestUserData(getUserFresh(authStore.currentUser.id))
          }
          break

        case 'logout':
          setAPIToken(null)
          setRefreshToken(null)

          localStorage.removeItem(LOCAL_STORAGE_KEYS.session)
          cookies.removeAll()

          router.push('/')

          break
      }
    })
  })

  // Restore the session from localStorage (client-only). The session is never
  // stored in a cookie, so the user token is never transmitted to the SSR server.
  if (import.meta.client) {
    const migration = migrateLegacySessionCookie(cookies, localStorage)

    if (migration) {
      const { trackEvent } = useAnalyticsTrackingService()

      // Deferred so the analytics services (initialized in 02.services) are ready.
      onNuxtReady(() => {
        trackEvent(ANALYTICS_EVENTS.sessionCookieMigrated(migration.migratedSession))
      })
    }

    const stored = localStorage.getItem(LOCAL_STORAGE_KEYS.session)

    if (stored) {
      try {
        const sessionData = JSON.parse(stored)

        if (sessionData && sessionData.token) {
          await authStore.persistSession(sessionData)
        }
      } catch {
        navigateTo('/')
      }
    }
  }
})
